// Canonical motion variants for the Studio motion system.
//
// Plan ref: 06-01-PLAN-motion-primitives-view-transitions.md §Task 1
// Research: 06-RESEARCH-motion-responsive-a11y.md §Pattern 1
// REQ:      UI-MOTION-01, UI-MOTION-03
//
// 4 motion roles, one variant + one transition per role:
//   - Reveal      — list / card entry (opacity + y:8 → 0)
//   - Affordance  — hover / press feedback on buttons + tiles
//   - Transition  — route fallback when View Transitions API unsupported
//   - Feedback    — toast / mutation outcome (Linear-feel spring)
//
// Components SHOULD import these instead of authoring inline variants. If a
// surface needs a different feel, document the override in
// studio/docs/design/motion.md (per-component override table).

import type { Variants, Transition } from 'motion/react';
import { LINEAR_SPRING, REDUCED_TRANSITION } from './springs';

// --- Reveal -----------------------------------------------------------------
// Cards and list rows fade up 8px on entry. Budget: <120ms.
export const reveal: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

// 120ms with an ease-out curve (cubic bezier ~ ease-out-soft).
export const revealTransition: Transition = {
  duration: 0.12,
  ease: [0.2, 0.0, 0.0, 1.0],
};

// 30ms cascade — subtle, never jarring. Disabled under reduced-motion.
export const revealStagger: Transition = {
  staggerChildren: 0.03,
  delayChildren: 0.0,
};

// --- Affordance -------------------------------------------------------------
// Buttons, tile hovers, key-press feedback. Instant — never spring-y.
export const affordance: Variants = {
  rest: { opacity: 0.85, scale: 1 },
  hover: { opacity: 1, scale: 1 },
  pressed: { opacity: 0.95, scale: 0.99 },
};

export const affordanceTransition: Transition = {
  duration: 0.08,
  ease: 'linear',
};

// --- Transition (fallback) --------------------------------------------------
// Used when document.startViewTransition is unavailable. Crossfades only.
export const transitionFallback: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

export const transitionFallbackTransition: Transition = {
  duration: 0.18,
  ease: [0.2, 0, 0, 1],
};

// --- Feedback ---------------------------------------------------------------
// Toasts + mutation outcomes. Spring-driven for organic feel.
export const feedback: Variants = {
  hidden: { opacity: 0, y: 12, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1 },
  exiting: { opacity: 0, y: 4, scale: 0.99 },
};

export const feedbackTransition: Transition = LINEAR_SPRING;

// --- Reduced-motion fallback ------------------------------------------------
// Re-export so consumers can short-circuit by swapping a single transition.
export const reducedMotionTransition: Transition = REDUCED_TRANSITION;
